import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { AUTH_API_ROUTES } from '../constants/auth.constant';
import { HeaderService } from './header.service';
import { HttpRoutingService } from './http-routing.service';
import { StaffStateService } from './staff-state.service';
import type {
  LoginResponseModel,
  AuthApiResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyTokenRequest,
  UserRolePermissionResponse,
} from '../models/auth.model';

/**
 * Provides authentication-related operations for the application.
 * Handles user login, password recovery, token verification, password reset,
 * authenticated staff session restoration, access-token refresh, authentication
 * state checks, token retrieval, and logout operations.
 * The service also synchronizes authenticated staff information with
 * `StaffStateService` when the current session is restored.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  /**
   * Provides HTTP methods used to communicate with backend authentication APIs.
   */
  private readonly httpRoutingService = inject(HttpRoutingService);

  /**
   * Provides access to configured HTTP headers for authentication requests.
   */
  private readonly headerService = inject(HeaderService);

  /**
   * Maintains the authenticated staff member's application state.
   */
  private readonly staffStateService = inject(StaffStateService);

  /**
   * Authenticates a user using their email address and password.
   * The credentials are encoded using Base64 and sent through the
   * `Registration` request header.
   * @param email - Email address used for authentication.
   * @param password - Password used for authentication.
   * @returns Observable containing the authentication API response
   * and login information.
   */
  login(email: string,password: string): Observable<AuthApiResponse<LoginResponseModel>> {
    const authHeader = `Basic ${btoa(`${email}:${password}`)}`;
    const headers = this.headerService.getHeaders(AUTH_API_ROUTES.SIGN_IN).set('Registration', authHeader);
    return this.httpRoutingService.postMethod(AUTH_API_ROUTES.SIGN_IN, {}, undefined, headers);
  }

  /**
   * Sends a forgot-password request to the authentication API.
   * @param payload - Request payload containing the information
   * required to initiate password recovery.
   * @returns Observable containing the authentication API response.
   */
  forgotPassword(payload: ForgotPasswordRequest): Observable<AuthApiResponse> {
    return this.httpRoutingService.postMethod(
      AUTH_API_ROUTES.FORGOT_PASSWORD,
      payload
    );
  }

  /**
   * Verifies a password reset or authentication token.
   * @param payload - Token verification request payload.
   * @returns Observable containing the authentication API response.
   */
  verifyToken(payload: VerifyTokenRequest): Observable<AuthApiResponse> {
    return this.httpRoutingService.postMethod(
      AUTH_API_ROUTES.VERIFY_TOKEN,
      payload
    );
  }

  /**
   * Resets the user's password using the supplied token and password.
   * @param payload - Password reset request containing the reset token
   * and new password.
   * @returns Observable containing the authentication API response.
   */
  resetPassword(payload: ResetPasswordRequest): Observable<AuthApiResponse> {
    return this.httpRoutingService.putMethod(
      AUTH_API_ROUTES.RESET_PASSWORD,
      payload
    );
  }

  /**
   * Fetches the authenticated staff member's profile and
   * role/permission details.
   * The backend resolves the staff identity from the JWT, so
   * no staff identifier is required in the request.
   * @returns Observable containing the authenticated staff member's
   * role and permission information.
   */
  getUserRolePermission(): Observable<AuthApiResponse<UserRolePermissionResponse>> {
    return this.httpRoutingService.getMethod(AUTH_API_ROUTES.USER_ROLE_PERMISSION);
  }

  /**
   * Restores the authenticated staff session from the server.
   * This is useful after a full browser refresh where the in-memory
   * staff signal state has been lost but the access token remains
   * available in local storage.
   * If no JWT token exists, the method immediately completes without
   * making an API request.
   * When staff information is successfully retrieved, it is stored
   * in `StaffStateService`.
   * API errors are handled silently and result in an empty completion
   * so that application initialization is not interrupted.
   * @returns Observable that completes with `void`.
   */
  restoreStaffSession(): Observable<void> {
    if (!this.isAuthenticated()) {
      return of(void 0);
    }
    return this.getUserRolePermission().pipe(
      tap((response: AuthApiResponse<UserRolePermissionResponse>) => {
        const staff = response?.data?.staff;
        if (staff) {
          this.staffStateService.setStaff(staff);
        }
      }),
      map(() => void 0),

      /**
       * Prevents session restoration errors from blocking
       * application initialization.
       */
      catchError(() => of(void 0))
    );
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * If no refresh token exists, the method immediately returns
   * `false`. When a new access token is returned by the backend,
   * it replaces the existing JWT token in local storage.
   * @returns Observable containing `true` when the access token
   * is successfully refreshed; otherwise, `false`.
   */
  refreshToken(): Observable<boolean> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return of(false);
    }

    return this.httpRoutingService
      .postMethod(
        AUTH_API_ROUTES.REFRESH_TOKEN,
        { refreshToken }
      )
      .pipe(
        /**
         * Stores the newly generated access token when available.
         */
        tap((response: AuthApiResponse<string>) => {
          const newAccessToken = response?.data;

          if (newAccessToken) {
            localStorage.setItem(
              'jwt_token',
              newAccessToken
            );
          }
        }),

        /**
         * Converts the API response into a boolean indicating
         * whether a new access token was received.
         */
        map(
          (response: AuthApiResponse<string>) =>
            !!response?.data
        ),

        /**
         * Converts refresh-token failures into a `false` result.
         */
        catchError(() => of(false))
      );
  }

  /**
   * Checks whether the user currently has an access token.
   * @returns `true` when a JWT token exists in local storage;
   * otherwise, `false`.
   */
  isAuthenticated(): boolean {
    return !!this.getJwtToken();
  }

  /**
   * Retrieves the JWT access token from browser local storage.
   * @returns The stored JWT token, or `null` when no token exists.
   */
  getJwtToken(): string | null {
    return localStorage.getItem('jwt_token');
  }
  /**
   * Logs out the current authenticated user.
   * Clears the staff state and removes both the access token
   * and refresh token from local storage.
   */
  logout() {
    this.staffStateService.clearStaff();
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refreshToken');
  }
}